import { useState, useCallback, useEffect } from 'react';
import { ProductoService } from '../services';
import {
  Producto,
  CreateProductoInput,
  UpdateProductoInput,
} from '../types';

export { useFormularioProducto } from './useFormularioProducto';
export { useDragAndDrop } from './useDragAndDrop';
export { useProveedores } from './useProveedores';
export { useInactivarProducto } from './useInactivarProducto';

export function useProductos(initialPage: number = 1) {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [prevSearch, setPrevSearch] = useState<string | undefined>();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== (search || '')) {
        setSearch(searchQuery || undefined);
        setPage(1);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, search]);

  // Fetch productos with append mode support
  const fetchProductos = useCallback(async (appendMode = false) => {
    if (appendMode) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const resultado = await ProductoService.listarProductos(page, limit, search);

      if (appendMode) {
        // Append mode: agregar a productos existentes
        setProductos((prev) => [...prev, ...resultado.items]);
      } else {
        // Replace mode: reemplazar completamente
        setProductos(resultado.items);
      }

      setTotal(resultado.total);
      setTotalPages(resultado.totalPages);
      setPrevSearch(search);
    } catch (err: any) {
      setError(err.message || 'Error al cargar productos');
    } finally {
      if (appendMode) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [page, limit, search]);

  // Handle page/limit/search changes
  useEffect(() => {
    // Si search cambió, usar replace mode. Si solo página cambió, usar append mode
    const appendMode = productos.length > 0 && search === prevSearch;
    fetchProductos(appendMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search]);

  // Calculate hasMore
  const hasMore = productos.length < total;

  // fetchMore for infinite scroll
  const fetchMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setPage((prev) => prev + 1);
  }, [loadingMore, hasMore]);

  const deleteProducto = useCallback(
    async (id: number) => {
      try {
        await ProductoService.eliminarProducto(id);
        setProductos((prev) => prev.filter((p) => p.id !== id));
        setTotal((prev) => prev - 1);
      } catch (err: any) {
        setError(err.message || 'Error al eliminar producto');
        throw err;
      }
    },
    []
  );

  const searchProductos = useCallback((query: string | undefined) => {
    setSearch(query);
    setPage(1);
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const changeLimit = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  const updateSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearch(undefined);
    setPage(1);
  }, []);

  return {
    productos,
    loading,
    loadingMore,
    error,
    page,
    limit,
    total,
    totalPages,
    searchQuery,
    hasMore,
    deleteProducto,
    searchProductos,
    changePage,
    changeLimit,
    updateSearchQuery,
    clearSearch,
    fetchMore,
    refetch: fetchProductos,
  };
}

export function useProductoForm(
  initialData?: Producto,
  onSuccess?: (producto: Producto) => void
) {
  const [formData, setFormData] = useState<CreateProductoInput | UpdateProductoInput>(
    initialData
      ? {
          nombre: initialData.nombre,
          descripcion: initialData.descripcion,
          precioBase: initialData.precioBase,
          precioVenta: initialData.precioVenta,
          cantidadStock: initialData.cantidadStock,
          stockMinimo: initialData.stockMinimo,
          activo: initialData.activo,
          categoriaId: initialData.categoriaId ?? null,
          subcategoriaId: initialData.subcategoriaId ?? null,
        }
      : {
          codigo: '',
          nombre: '',
          descripcion: '',
          precioBase: 0,
          precioVenta: 0,
          cantidadStock: 0,
          stockMinimo: 0,
        }
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        nombre: initialData.nombre,
        descripcion: initialData.descripcion,
        precioBase: initialData.precioBase,
        precioVenta: initialData.precioVenta,
        cantidadStock: initialData.cantidadStock,
        stockMinimo: initialData.stockMinimo,
        activo: initialData.activo,
        categoriaId: initialData.categoriaId ?? null,
        subcategoriaId: initialData.subcategoriaId ?? null,
      });
      setError(null);
    }
  }, [initialData]);

  const handleChange = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      setError(null);

      try {
        let resultado: Producto;

        if (initialData) {
          // Filtrar solo campos permitidos en UpdateProductoInput
          const updateData: UpdateProductoInput = {
            nombre: (formData as any).nombre,
            descripcion: (formData as any).descripcion,
            precioBase: (formData as any).precioBase,
            precioVenta: (formData as any).precioVenta,
            cantidadStock: (formData as any).cantidadStock,
            stockMinimo: (formData as any).stockMinimo,
            activo: (formData as any).activo,
            categoriaId: (formData as any).categoriaId ?? null,
            subcategoriaId: (formData as any).subcategoriaId ?? null,
          };
          resultado = await ProductoService.actualizarProducto(
            initialData.id,
            updateData
          );
        } else {
          resultado = await ProductoService.crearProducto(
            formData as CreateProductoInput
          );
        }

        onSuccess?.(resultado);
      } catch (err: any) {
        setError(err.message || 'Error al guardar producto');
      } finally {
        setLoading(false);
      }
    },
    [initialData, formData, onSuccess]
  );

  return {
    formData,
    loading,
    error,
    handleChange,
    handleSubmit,
  };
}